import { Controller } from '@hotwired/stimulus';
import { initMask } from '../../packs/mask';
import { selectselectize } from '../../packs/custom_selectize';
import {
  driverSelectize,
  watchDriverSelectizeTyping,
} from '../../custom-selectize/selectize-driver';
import {
  vehicleSelectize,
  watchVehicleSelectizeTyping,
} from '../../custom-selectize/selectize-vehicle';
import {
  canDriverTransport,
  canVehicleTransport,
} from '../../validate_trip_requirements';

export default class extends Controller {
  static get targets() {
    return ['frame', 'content', 'saveButton'];
  }

  static get values() {
    return {
      st: String,
      providersUrl: String,
      pricesUrl: String,
      broken: { type: Boolean, default: false },
    };
  }

  initialize() {
    this.selectListeners = [];
    this.requests = new Map();
  }

  disconnect() {
    this.releaseContent();
  }

  contentTargetConnected() {
    initMask();
    selectselectize();
    driverSelectize();
    vehicleSelectize('truck', 'Cavalo...');
    vehicleSelectize('trailer', 'Carreta...');

    this.bindSelect('.transporter', (target) => {
      this.changeProvider(target, 'manual');
      this.updateDisplayText(target, 'service_provider_id');
    });
    this.bindSelect('.transporter_pricing', (target) => {
      this.changeProvider(target, 'automatic');
      this.updateDisplayText(target, 'financial_service_provider_id');
    });
    this.bindSelect('.driver', (target) => {
      canDriverTransport(target);
      this.updateDisplayText(target, 'driver_id');
    });
    ['truck', 'trailer'].forEach((type) => {
      this.bindSelect(`.${type}`, (target) => {
        canVehicleTransport(target, type);
        this.updateDisplayText(target, `${type}_id`);
      });
      this.fields(`select.${type}`).each((_i, field) => {
        watchVehicleSelectizeTyping(field.dataset.index, type);
      });
    });
    this.fields('select.driver').each((_i, field) => {
      watchDriverSelectizeTyping(field.dataset.index);
    });
    this.populateDriverSchedulesTable();
  }

  contentTargetDisconnected() {
    this.releaseContent();
  }

  releaseContent() {
    this.selectListeners.splice(0).forEach(({ selectize, handler }) => {
      selectize.off('change', handler);
    });
    this.requests.forEach((request) => request.abort());
    this.requests.clear();
    if (this.driverTripsRequest) this.driverTripsRequest.abort();
  }

  fields(selector) {
    return $(this.element).find(selector);
  }

  bindSelect(selector, callback) {
    this.fields(`select${selector}`).each((_i, field) => {
      const { selectize } = field;
      const handler = () => callback(field);
      selectize.on('change', handler);
      this.selectListeners.push({ selectize, handler });
    });
  }

  selectizes(index) {
    return ['driver', 'truck', 'trailer'].map(
      (type) =>
        this.fields(`select.${type}[data-index='${index}']`)[0].selectize
    );
  }

  updateDisplayText(target, attribute) {
    const nextIndex = Number(target.dataset.index) + 1;
    const selector = `#reservation_container_reservation_container_trips_attributes_${nextIndex}_schedule_attributes_${attribute}`;
    this.fields(`${selector}[type='hidden']`).val(target.value);
    this.fields(
      `#${attribute.replace('_id', '_text')}[data-index='${nextIndex}']`
    ).text($(target).find(':selected').text());
  }

  showNewScheduleDate({ currentTarget: target }) {
    const { index } = target.dataset;
    this.fields(`.new_schedule_date_fields_${index}`).toggleClass(
      'd-none',
      !target.checked
    );
    if (!target.checked) {
      this.fields(
        `#reservation_container_reservation_container_trips_attributes_${index}_new_schedule_date`
      ).val('');
    }
  }

  toggleManualPricing({ currentTarget: target }) {
    const { finc } = target.dataset;
    const manual = target.checked;
    this.fields(`.manual[data-finc='${finc}']`).toggleClass('d-none', !manual);
    this.fields(
      `.not-manual[data-finc='${finc}'], .precify_update[data-finc='${finc}']`
    ).toggleClass('d-none', manual);
    ['transporter_pricing', 'transporter'].forEach((type) => {
      this.fields(`select.${type}[data-finc='${finc}']`).each((_i, field) => {
        const enabled =
          type === 'transporter'
            ? manual
            : !manual && !field.classList.contains('not-complete');
        field.selectize[enabled ? 'enable' : 'disable']();
        const wasEmpty = !field.value;
        field.selectize.clear();
        if (wasEmpty) {
          this.changeProvider(
            field,
            type === 'transporter' ? 'manual' : 'automatic'
          );
          this.updateDisplayText(
            field,
            type === 'transporter'
              ? 'service_provider_id'
              : 'financial_service_provider_id'
          );
        }
      });
    });
    this.fields(`.manual_pricing_hidding[data-finc='${finc}']`).val(manual);
    this.fields(`.supplier-price[data-finc='${finc}']`)
      .prop('required', manual)
      .prop('disabled', !manual)
      .val('');
  }

  async changeProvider(target, type) {
    const { index, finc } = target.dataset;
    const key = `provider-${index}`;
    if (this.requests.has(key)) this.requests.get(key).abort();
    this.requests.delete(key);
    const [driver, truck, trailer] = this.selectizes(index);
    [driver, truck, trailer].forEach((selectize) => {
      selectize.clear();
      selectize.clearOptions();
      selectize.disable();
    });
    this.fields(
      `.freight_supplier_price[data-finc='${finc}'], .toll_supplier_price[data-finc='${finc}']`
    )
      .val('')
      .prop('required', target.value !== '');
    if (!target.value) return;

    const request = new AbortController();
    this.requests.set(key, request);
    try {
      const provider = await this.fetchJson(
        this.providersUrlValue,
        {
          precification_id: target.value,
          type,
          st: this.stValue,
        },
        request.signal
      );
      if (request.signal.aborted) return;

      const locked =
        driver.$input[0].dataset.hasSm === 'true' ||
        driver.$input[0].dataset.lastRct === 'true';
      if (!locked) {
        driver.addOption(provider.drivers);
        trailer.addOption(provider.trailers);
        [driver, truck, trailer].forEach((selectize) => selectize.enable());
      }
      const trucks =
        truck.$input[0].dataset.stClient === 'NESTLÉ'
          ? provider.trucks.filter((vehicle) => vehicle.sem_parar_tag !== false)
          : provider.trucks;
      truck.addOption(trucks);
      if (type !== 'manual') {
        this.setPrices(
          finc,
          provider.freight_value,
          provider.toll_value,
          provider.freight_value.replace('.', '').replace(',', '.'),
          provider.toll_value.replace('.', '').replace(',', '.')
        );
      }
    } catch (error) {
      if (!request.signal.aborted) {
        console.error(error);
        alert(
          'Não foi possível carregar motoristas e veículos. Selecione a transportadora novamente.'
        );
      }
    } finally {
      if (this.requests.get(key) === request) this.requests.delete(key);
    }
  }

  setPrices(finc, freight, toll, realFreight, realToll) {
    this.fields(`.freight_supplier_price[data-finc='${finc}']`)
      .prop('required', true)
      .val(freight);
    this.fields(`.toll_supplier_price[data-finc='${finc}']`)
      .prop('required', true)
      .val(toll);
    this.fields(`.real-value-freight-${finc}`).val(realFreight);
    this.fields(`.real-value-toll-${finc}`).val(realToll);
  }

  async updatePrecify({ currentTarget: button }) {
    const { finc } = button.dataset;
    const key = `prices-${finc}`;
    if (this.requests.has(key)) return;
    const request = new AbortController();
    this.requests.set(key, request);
    this.setPricingLoading(button, true);
    try {
      const { response } = await this.fetchJson(
        this.pricesUrlValue,
        { finc_id: finc },
        request.signal
      );
      if (request.signal.aborted) return;
      if (response.error) {
        this.fields('.pricing_error')
          .text(response.message)
          .removeClass('d-none');
        this.fields('.pricing_message').addClass('d-none');
        return;
      }
      this.fields(`select.transporter_pricing[data-finc='${finc}']`).each(
        (_i, field) => {
          field.selectize.clearOptions();
          field.selectize.addOption(response.service_providers);
        }
      );
      if (response.freight_price != null && response.toll_price != null) {
        this.setPrices(
          finc,
          response.freight_price.toString().replace('.', ','),
          response.toll_price.toString().replace('.', ','),
          response.freight_price,
          response.toll_price
        );
      }
      this.fields('.pricing_message')
        .text(`Precificação realizada em: ${response.time_updated}`)
        .removeClass('d-none');
      this.fields('.pricing_error').addClass('d-none');
    } catch (error) {
      if (!request.signal.aborted) {
        console.error(error);
        alert('Não foi possível atualizar a precificação. Tente novamente.');
      }
    } finally {
      this.requests.delete(key);
      if (!request.signal.aborted) this.setPricingLoading(button, false);
    }
  }

  setPricingLoading(button, loading) {
    button.disabled = loading;
    $(button).find('span').toggleClass('d-none', !loading);
    this.saveButtonTargets.forEach((saveButton) => {
      saveButton.disabled =
        loading ||
        Array.from(this.requests.keys()).some((key) =>
          key.startsWith('prices-')
        );
    });
  }

  async fetchJson(url, params, signal) {
    const response = await fetch(`${url}?${new URLSearchParams(params)}`, {
      signal,
    });
    if (!response.ok)
      throw new Error(`Falha ao carregar ${url}: HTTP ${response.status}`);
    return response.json();
  }

  populateDriverSchedulesTable() {
    const drivers = this.fields('select.driver')
      .toArray()
      .filter((field) => field.value);
    const lastDriver = drivers[drivers.length - 1];
    if (lastDriver) {
      this.driverTripsRequest = $.get('/drivers/trips', {
        driver_id: lastDriver.value,
      }).fail((_xhr, status) => {
        if (status !== 'abort')
          alert('Não foi possível carregar as viagens do motorista.');
      });
    }
  }

  frameFailed(event) {
    if (event.target !== this.frameTarget) return;
    event.preventDefault();
    this.brokenValue = true;
    this.frameTarget.innerHTML =
      '<div class="text-center py-5">' +
      '<p class="text-danger mb-2">Não foi possível carregar a programação.</p>' +
      '<button type="button" class="btn btn-sm btn-outline-dark text-uppercase" ' +
      'data-action="forms--schedule#retry">tentar de novo</button></div>';
  }

  retry() {
    this.brokenValue = false;
    this.frameTarget.reload();
  }

  preventBrokenSubmit(event) {
    const form =
      this.element.closest('form') || this.frameTarget.querySelector('form');
    if (event.target !== form || !this.brokenValue) return;
    event.preventDefault();
    alert('A programação não carregou. Recarregue antes de salvar.');
  }
}
